/**
 * Utility functions for BookletView component
 */

/**
 * Format date string to Japanese long format
 * Example: "2024年3月15日(金)"
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
  });
}

/**
 * Format date string to Japanese short format
 * Example: "3月15日(金)"
 */
export function formatDateShort(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("ja-JP", {
    month: "short",
    day: "numeric",
    weekday: "short",
  });
}

/**
 * Calculate the number of days in a trip (inclusive of both start and end dates)
 * @returns Number of days, or null if dates are missing
 */
export function getDayCount(startDate: string | null, endDate: string | null): number | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1;
}

/**
 * Calculate the number of days until the trip starts
 * @param startDate - Trip start date string
 * @param today - Current date (for testing)
 * @returns Number of days until trip, or null if startDate is missing
 *          Positive = trip is in the future
 *          Zero = trip starts today
 *          Negative = trip has already started
 */
export function getDaysUntilTrip(
  startDate: string | null,
  today: Date = new Date()
): number | null {
  if (!startDate) return null;
  const start = new Date(startDate);
  const todayNormalized = new Date(today);
  todayNormalized.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  const diffTime = start.getTime() - todayNormalized.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Format amount as Japanese Yen currency
 * Example: "¥1,234"
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency: "JPY",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Calculate packing list progress
 * @returns Object with packed count, total count, and percentage
 */
export function calculatePackingProgress(items: { isPacked: boolean }[]): {
  packed: number;
  total: number;
  percentage: number;
} {
  const total = items.length;
  const packed = items.filter((item) => item.isPacked).length;
  const percentage = total > 0 ? Math.round((packed / total) * 100) : 0;
  return { packed, total, percentage };
}

/**
 * Sort packing items by priority (high to low)
 */
export function sortPackingItemsByPriority<T extends { priority: number | null; name: string }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => {
    const aPriority = a.priority ?? 0;
    const bPriority = b.priority ?? 0;
    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }
    return a.name.localeCompare(b.name, "ja");
  });
}

/**
 * Count high priority items (priority >= 2)
 */
export function countHighPriorityItems<T extends { priority: number | null }>(items: T[]): number {
  return items.filter((item) => item.priority && item.priority >= 2).length;
}

/**
 * Calculate total budget from spots
 */
export function calculateTotalBudget(spots: { cost: number | null }[] | undefined): number {
  if (!spots) return 0;
  return spots.reduce((sum, spot) => sum + (spot.cost || 0), 0);
}
